import React, { Fragment, useMemo } from 'react';

import { getReviewsSchema, ReviewItem } from '@zola-helpers/client/dist/es/schema';
import { SearchLocationReviewView } from '@zola/svc-marketplace-ts-types';
import LinkV2 from '@zola/zola-ui/src/components/LinkV2/LinkV2';
import Schemas from '@zola/zola-ui/src/components/Schemas/Schemas';
import StarRatings from '@zola/zola-ui/src/components/StarRatings/StarRatings';
import { TileScroller, TilesWrapper } from '@zola/zola-ui/src/components/TileScroller';
import { ViewMore } from '@zola/zola-ui/src/components/ViewMore/ViewMore';
import { COLORS3 } from '@zola/zola-ui/src/styles/emotion';
import { COLORS_SEMANTIC } from '@zola/zola-ui/src/styles/emotion/colors3';
import { Title2 } from '@zola/zola-ui/src/typography/Headings';
import { BodySmall } from '@zola/zola-ui/src/typography/Paragraphs';

import { css } from '@emotion/react';
import cx from 'classnames';

import { isSearchLocation } from '~/util/searchUtils';

import { useSearchResultsPageContext } from '../../contexts/SearchResultsPageContext';

import styles from './searchLocationReviews.module.less';

/**
 * A Review for this search location.  This is missing title and the couple's
 * location from the designs as that data was not initially returned.
 */
const ReviewTile = ({ review }: { review: SearchLocationReviewView }) => {
  return (
    <div className={styles.tile}>
      <div className={styles.locationAndRating}>
        <BodySmall className={styles.reviewerInfo}>{review.reviewerName}</BodySmall>
        <StarRatings
          activeStars={review.overallRating}
          showReviewCount={false}
          totalStars={5}
          reviewCount={''}
          fillColor={COLORS_SEMANTIC.POSITIVE_100}
          starSize="large"
        />
      </div>
      <div className={styles.tileContent}>
        <ViewMore
          lines={7}
          backgroundColor={COLORS3.WHITE_100}
          viewLess={() => <Fragment />}
          viewMore={({ setIsOpen }) => (
            <LinkV2
              sizes="smaller"
              role="button"
              noTextTransform
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsOpen(true);
              }}
            >
              Read more
            </LinkV2>
          )}
        >
          {review.reviewText}
        </ViewMore>
      </div>
    </div>
  );
};

/**
 * Add some custom presentation to the tile scroller.  Most of these can be found
 * as examples in the storybook
 *
 * - Center the cards on the page
 * - Adjust padding and gap
 */
const tileScroller = css`
  justify-content: center;

  ${TilesWrapper} {
    min-width: initial;
    padding-top: 0;
    padding-bottom: 16px;
    gap: 16px;
  }
`;

/**
 * Zola reviews for a search location.
 */
export const SearchLocationReviews = (props: React.HTMLAttributes<HTMLElement>) => {
  const { className, ...rest } = props;
  const { searchLocationReviews, vendorSearch } = useSearchResultsPageContext();

  const { selectedLocation } = vendorSearch;

  const location = useMemo(
    () => (isSearchLocation(selectedLocation) ? selectedLocation.label : undefined),
    [selectedLocation]
  );
  const schema = useMemo(() => {
    const reviews: ReviewItem[] = (searchLocationReviews?.reviews || []).map((review) => {
      return {
        reviewBody: review.reviewText,
        author: {
          '@type': 'Person',
          name: review.reviewerName,
        },
        itemReviewed: {
          '@type': 'Organization',
          name: 'Zola Vendor Marketplace',
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: review.overallRating,
          bestRating: 5,
          worstRating: 1,
          author: {
            '@type': 'Person',
            name: review.reviewerName,
          },
        },
      };
    });
    return {
      ...getReviewsSchema(reviews),
      description: `Top reviews from Zola Couples${location ? ` in ${location}` : ''}`,
    };
  }, [location, searchLocationReviews]);

  if (
    selectedLocation &&
    searchLocationReviews &&
    (searchLocationReviews.reviews || []).length > 0
  ) {
    return (
      <div className={cx(styles.container, className)} {...rest}>
        <Schemas schemas={schema} />
        <div className={styles.hed}>
          <Title2 className={styles.title} presentation="h4">
            Top reviews from Zola couples
            {location ? <span className={styles.noWrap}>&nbsp;in {location}</span> : ''}
          </Title2>
          <BodySmall color="BLACK_075" className={styles.dek}>
            Based on {searchLocationReviews.totalReviewCount} certified reviews, couples rated
            Zola’s service for finding a wedding venue{' '}
            {Math.floor((searchLocationReviews.averageOverallRating || 0) * 10) / 10.0} out of 5 on
            average.
          </BodySmall>
        </div>
        <TileScroller css={tileScroller} scrollCenter>
          {(searchLocationReviews?.reviews || []).map((review, index) => (
            <ReviewTile key={index} review={review} />
          ))}
        </TileScroller>
      </div>
    );
  }
  return <Fragment />;
};
